import React from 'react';

import { _ } from '../../../../libs/lodash';

const MAX_WIDTH_FOR_CENTER = 120;
const RESIZE_DEBOUNCE_MS = 500;

type UseMaxItemsPerRowOptions = {
  shouldCenter: boolean;
  showToggleAll: boolean;
};

export const useMaxItemsPerRow = (
  containerRef: React.RefObject<HTMLDivElement>,
  seriesKeys: string[],
  columnGap = 10,
  options: UseMaxItemsPerRowOptions
): { maxItemsPerRow: number; isReady: boolean; shouldCenter: boolean } => {
  const { shouldCenter: shouldCenterProp, showToggleAll } = options;
  const [maxItemsPerRow, setMaxItemsPerRow] = React.useState(4);
  const [isReady, setIsReady] = React.useState(false);
  const [shouldCenter, setShouldCenter] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    const calculateMaxItems = () => {
      if (!containerRef.current) {
        if (isMounted) setIsReady(false);
        return;
      }

      const container = containerRef.current;
      const containerWidth = container.offsetWidth - 64; // Subtract padding (px: 8 = 32px each side)

      if (containerWidth <= 0) {
        if (isMounted) setIsReady(false);
        return;
      }

      // Create a temporary element to measure item width
      const tempElement = document.createElement('div');
      tempElement.style.position = 'absolute';
      tempElement.style.visibility = 'hidden';
      tempElement.style.whiteSpace = 'nowrap';
      tempElement.style.fontSize = '12px';
      tempElement.style.padding = '4px';
      tempElement.style.display = 'inline-flex';
      tempElement.style.alignItems = 'center';
      tempElement.style.gap = '6px';

      // Use the longest series name to estimate max width
      const longestName = seriesKeys.reduce((a, b) => (a.length > b.length ? a : b), '');
      tempElement.textContent = longestName;
      document.body.appendChild(tempElement);

      const itemWidth = tempElement.offsetWidth + 12; // Add square width (12px)
      document.body.removeChild(tempElement);

      const parentWidth = container.offsetWidth;
      const effectiveShouldCenter = shouldCenterProp && parentWidth <= MAX_WIDTH_FOR_CENTER;

      // Calculate how many items fit: (containerWidth + gap) / (itemWidth + gap)
      const itemsThatFit = Math.floor((containerWidth + columnGap) / (itemWidth + columnGap));
      const calculatedMax = Math.max(1, Math.min(itemsThatFit, seriesKeys.length));

      if (isMounted) {
        setMaxItemsPerRow(calculatedMax);
        setShouldCenter(effectiveShouldCenter);
        setIsReady(true);
      }
    };

    const debouncedCalculate = _.debounce(calculateMaxItems, RESIZE_DEBOUNCE_MS);

    calculateMaxItems();

    const container = containerRef.current;
    if (!container) {
      return () => {
        isMounted = false;
        debouncedCalculate.cancel();
      };
    }

    const resizeObserver = new ResizeObserver(debouncedCalculate);
    resizeObserver.observe(container);

    return () => {
      isMounted = false;
      debouncedCalculate.cancel();
      resizeObserver.disconnect();
    };
  }, [containerRef, seriesKeys, columnGap, shouldCenterProp, showToggleAll]);

  return { maxItemsPerRow, isReady, shouldCenter };
};
